import React, { useEffect, useState } from "react";
import axios from "axios";
import CodexWorkspace from "./CodexWorkspace";
import SshTermWindow from "./SshTermWindow";
import { API_BASE, THEMES } from "./config/themes";
import { authHeaders, getApiErrorMessage } from "./api/client";
import { globalStyle } from "./utils/appStyles";

const adminTheme = THEMES.tobe_staff || THEMES.general;

// 독립 진입점: https://ai.to-be.kr/codex — 서버·코드·에이전트·배포 통합 작업실
function CodexApp() {
  const [checking, setChecking] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loginData, setLoginData] = useState({ userid: "", password: "" });
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);   // 로그인은 됐지만 codex 권한이 없는 상태
  const [reqMsg, setReqMsg] = useState("");
  const [reqReason, setReqReason] = useState("");

  // 접근 판정은 백엔드가 결정한다: 최고관리자 OR 관리자가 '허락'한 사용자(codex_access).
  const enterWithAccessCheck = async (user) => {
    try {
      const res = await axios.get(`${API_BASE}/workspace/codex/access/me`, { headers: authHeaders() });
      if (!res.data?.allowed) {
        setIsAdmin(false); setIsLoggedIn(false); setCurrentUser(user); setDenied(true);
        setError("");
        return false;
      }
      setIsAdmin(!!res.data?.is_admin);
      setCurrentUser(user); setIsLoggedIn(true); setDenied(false); setError("");
      return true;
    } catch (err) {
      setIsLoggedIn(false); setCurrentUser(null); setDenied(false);
      setError("접근 권한 확인에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      return false;
    }
  };

  const requestAccess = async () => {
    try {
      const res = await axios.post(`${API_BASE}/workspace/codex/access/request`, { reason: reqReason.trim() }, { headers: authHeaders() });
      if (res.data?.already) { await enterWithAccessCheck(currentUser); return; }
      setReqMsg("접근 요청이 접수되었습니다. 관리자가 승인하면 알려드립니다.");
    } catch (err) { setReqMsg("요청 전송에 실패했습니다. 잠시 후 다시 시도해 주세요."); }
  };

  const logout = () => { localStorage.removeItem("token"); setDenied(false); setCurrentUser(null); setReqMsg(""); };

  const loadCurrentUser = async () => {
    try {
      const res = await axios.get(`${API_BASE}/me`, { headers: authHeaders() });
      const user = res.data?.user || res.data || null;
      await enterWithAccessCheck(user);  // 실패해도 토큰은 유지(승인 후 새로고침하면 진입)
    } catch (err) {
      localStorage.removeItem("token"); setIsLoggedIn(false); setCurrentUser(null);
    } finally { setChecking(false); }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) loadCurrentUser(); else setChecking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault(); setError("");
    try {
      const res = await axios.post(`${API_BASE}/login`, { userid: loginData.userid.trim(), password: loginData.password });
      localStorage.setItem("token", res.data?.token);
      await enterWithAccessCheck(res.data?.user);
    } catch (err) { setError(getApiErrorMessage(err, "로그인 정보를 확인해 주세요.")); }
  };

  if (checking) {
    return (
      <div className="auth-page admin-auth-page"><style>{globalStyle(adminTheme)}</style>
        <div className="auth-card"><h1>TOBE <span>Codex</span></h1><p className="auth-subtitle">권한을 확인하는 중입니다.</p></div>
      </div>
    );
  }

  if (!isLoggedIn && denied) {
    return (
      <div className="auth-page admin-auth-page"><style>{globalStyle(adminTheme)}</style>
        <div className="auth-card">
          <h1>TOBE <span>Codex</span></h1>
          <p className="auth-subtitle">아직 codex 사용 권한이 없습니다. 최고관리자의 승인이 필요합니다.</p>
          {reqMsg ? <div className="auth-error" style={{ background: "rgba(34,197,94,.12)", color: "#16a34a", borderColor: "rgba(34,197,94,.4)" }}>{reqMsg}</div> : (
            <>
              <input className="auth-input" placeholder="요청 사유(선택) — 예: OO 프로젝트 참여" value={reqReason} onChange={(e) => setReqReason(e.target.value)} />
              <button type="button" className="btn-auth" onClick={requestAccess}>접근 요청 보내기</button>
            </>
          )}
          <p className="auth-toggle" onClick={logout}>다른 계정으로 로그인</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="auth-page admin-auth-page"><style>{globalStyle(adminTheme)}</style>
        <form className="auth-card" onSubmit={handleLogin}>
          <h1>TOBE <span>Codex</span></h1>
          <p className="auth-subtitle">서버·코드·배포 통합 작업실 (최고관리자·승인된 사용자 전용)</p>
          <input className="auth-input" placeholder="아이디" value={loginData.userid} onChange={(e) => setLoginData({ ...loginData, userid: e.target.value })} />
          <input className="auth-input" type="password" placeholder="비밀번호" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} />
          {error && <div className="auth-error">{error}</div>}
          <button type="submit" className="btn-auth">로그인</button>
          <p className="auth-toggle" onClick={() => { window.location.href = "/admin"; }}>관리자 대시보드로 이동</p>
        </form>
      </div>
    );
  }

  const isTerm = typeof window !== "undefined" && (window.location.pathname === "/codex/term" || window.location.search.indexOf("target=") >= 0);
  if (isTerm) return <SshTermWindow apiBase={API_BASE} token={localStorage.getItem("token") || ""} />;
  return <CodexWorkspace apiBase={API_BASE} token={localStorage.getItem("token") || ""} currentUser={currentUser} isAdmin={isAdmin} />;
}

export default CodexApp;
